<script setup lang="ts">
import type { LayoutButton } from '~/types/elementsUi'
import type { RouteLocationRaw } from 'vue-router'
import type { HeadingType, StepItem } from '~/types/elementsUi'

interface StepsSectionProps {
  baseKey?: string
  title: string
  subtitle?: string
  items: StepItem[] | string
  buttons?: LayoutButton[]
  headingType?: HeadingType
}

const props = withDefaults(defineProps<StepsSectionProps>(), {
  buttons: undefined,
  headingType: 'h3'
})

const { t, te, tm } = useI18n(props.baseKey)

const computedTitle = computed(() => (te(props.title) ? t(props.title) : props.title))

const computedItems = computed<StepItem[]>(() => {
  if (typeof props.items === 'string') {
    return tm(props.items)
  }
  return props.items
})
</script>

<template>
  <VSection :title="computedTitle">
    <p v-if="subtitle" class="font-semibold">
      {{ te(subtitle) ? t(subtitle) : subtitle }}
    </p>

    <ol>
      <li v-for="(item, itemIndex) in computedItems" :key="`item-${itemIndex}`">
        <component :is="headingType" class="paragraph-title">{{ item.title }}</component>
        <p>
          {{ item.content }}
        </p>
      </li>
    </ol>

    <template #after>
      <slot name="after">
        <div
          v-for="(button, buttonIndex) in buttons"
          :key="`button-${buttonIndex}`"
          class="section-button"
        >
          <VButton
            :to="
              button.type === 'internal'
                ? ({ name: button.url } as RouteLocationRaw)
                : button.url
            "
            :label="button.label"
            :color="button.color"
            :disabled="button.disabled"
            @click="button.click && button.click()"
          />
        </div>
      </slot>
    </template>
  </VSection>
</template>

<style scoped></style>
